import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Wrench, Loader2, AlertTriangle, Check, Camera, Upload, FileText, Printer } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { TruckDiagram, truckParts, BODY_LABEL, DEFAULT_AXLES, DAMAGE_CODES, type DamageCode,
  type PartState, type AxleCount, type BodyType } from '@/components/inspections/TruckDiagram';
import { VehiclePicker } from '@/components/common/VehiclePicker';
import { PrintableInspectionSheet } from '@/components/inspections/PrintableInspectionSheet';
import { ConsumablesPicker, commitConsumables, EMPTY_CONSUMABLES,
  type ConsumableSelection } from '@/components/maintenance/ConsumablesPicker';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';

/**
 * Vehicle inspections — the fortnightly sheet, and the extraordinary one.
 *
 * Two tabs because they are two different jobs. The periodic is a walk-round
 * every 14 days. The extraordinary is done after work on the truck, carries the
 * full parts list and a diagram, and — if anything was changed — makes a
 * periodic due immediately, because the last routine check no longer describes
 * the vehicle.
 *
 * The due list leads, not the form. Opening this page should answer "what needs
 * doing" before it offers to record anything.
 */

type Kind = 'periodic' | 'extraordinary';
interface StatusRow {
  vehicle_id: string; plate_en: string | null; vehicle_code: string;
  side: string | null; driver_name: string | null;
  last_periodic_at: string | null; next_periodic_due: string | null;
  vehicle_status: string;
  due_reason: 'never' | 'after_change' | 'overdue' | 'ok' | 'inactive';
}
interface SheetItem { key: string; label: string }
interface HistoryRow {
  id: string; code: string; kind: Kind; inspected_at: string;
  result: string; changed_anything: boolean; what_changed: string | null;
  inspected_by_name: string | null;
}

const DUE_LABEL: Record<StatusRow['due_reason'], string> = {
  after_change: 'Due now — vehicle was changed',
  never: 'Never inspected',
  overdue: 'Overdue',
  ok: 'Up to date',
  // Listed so the side's count matches Settings > Sides, but not chased: a
  // truck out of service does not need a fortnightly walk-round.
  inactive: 'Out of service',
};
const DUE_TONE: Record<StatusRow['due_reason'], string> = {
  after_change: 'text-rose-600 dark:text-rose-400',
  never: 'text-amber-600 dark:text-amber-400',
  overdue: 'text-amber-600 dark:text-amber-400',
  ok: 'text-muted-foreground',
  inactive: 'text-muted-foreground',
};

const day = (s: string | null) => (s ? new Date(s).toISOString().slice(0, 10) : '—');

/**
 * The body, so the maintenance page can host it as a tab. Inspections and
 * maintenance are the same job seen twice - what a truck needs and what was
 * done to it - and two separate screens meant two places to look.
 */
const WORK_TYPES: Array<{ value: string; label: string }> = [
  { value: 'oil_change',       label: 'Oil change' },
  { value: 'tire_puncture',    label: 'Tire puncture' },
  { value: 'tire_replacement', label: 'Tire change' },
  { value: 'tire_wear',        label: 'Tire wear' },
  { value: 'basic_repair',     label: 'Basic repair' },
  { value: 'other',            label: 'Other' },
];

export function InspectionsPanel() {
  const qc = useQueryClient();
  // The side on screen. Every other page in this system scopes to it; this one
  // listed all 62 trucks regardless, which made a NEOM inspection round a
  // scrolling exercise.
  const { project } = useCurrentProject();
  const [kind, setKind] = useState<Kind>('periodic');
  const [vehicleId, setVehicleId] = useState('');
  const [odo, setOdo] = useState('');
  const [odoPrefilled, setOdoPrefilled] = useState(false);
  const [notes, setNotes] = useState('');
  const [whatChanged, setWhatChanged] = useState('');
  const [changed, setChanged] = useState(false);
  // The shape of the truck being inspected. Taken from the vehicle record
  // when one is picked, and changeable - the record is not always right, and
  // the inspector is looking at the vehicle.
  const [bodyType, setBodyType] = useState<BodyType>('other');
  const [axles, setAxles] = useState<AxleCount>(3);
  const [parts, setParts] = useState<Record<string, PartState>>({});
  // The damage letter per part, as on the printed sheet. Kept separate from
  // the state: 'fail' says a part is wrong, the letter says how.
  const [codes, setCodes] = useState<Record<string, DamageCode>>({});
  /** The part whose panel is open. Pressing the drawing opens it. */
  const [openPart, setOpenPart] = useState<string | null>(null);
  /** The paper version, for inspections walked with a clipboard. */
  const [printing, setPrinting] = useState(false);
  /**
   * Pictures already uploaded, per part. They are sent to the server the moment
   * they are taken - an inspector photographs damage as they find it, and a form
   * abandoned halfway must not take the evidence with it. The ids are claimed by
   * the inspection when it is saved.
   */
  const [photos, setPhotos] = useState<Record<string, { id: string }[]>>({});
  const [uploading, setUploading] = useState(false);
  /**
   * The work done at the same visit. An extraordinary inspection happens
   * because something was changed on the truck; the change usually cost money,
   * and asking for it here saves opening the maintenance page to say the same
   * thing twice.
   */
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [workCost, setWorkCost] = useState('');
  /** The bill, held until the maintenance record exists to hang it on. */
  const [bill, setBill] = useState<File | null>(null);
  /**
   * Parts fitted during the work, taken from the catalogue so stock is drawn
   * down by the same movement that records the fitting. Typing a part name into
   * a note leaves the shelf count wrong and nobody the wiser.
   */
  const [usedParts, setUsedParts] = useState<Array<{
    id: string; name: string; qty: number; cost: number; stock: number;
    /** Bought for this job rather than taken off a shelf. */
    free: boolean;
  }>>([]);
  const [partSearch, setPartSearch] = useState('');
  /** Oil drawn and tyres fitted during this job. */
  const [consumables, setConsumables] = useState<ConsumableSelection>(EMPTY_CONSUMABLES);

  /** Untick the change and the work goes with it, rather than logging silently. */
  const setChangedAndClear = (v: boolean) => {
    setChanged(v);
    if (!v) {
      setWorkTypes([]); setWorkCost(''); setBill(null); setUsedParts([]);
      setPartSearch(''); setConsumables(EMPTY_CONSUMABLES);
    }
  };
  const [checks, setChecks] = useState<Record<string, PartState>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data: sheets } = useQuery({
    queryKey: ['inspection-sheets'],
    queryFn: async () => (await apiClient.get<{
      periodic: { intervalDays: number; items: SheetItem[] };
      extraordinary: { items: SheetItem[] };
    }>('/api/v1/inspections/sheets')).data,
  });

  const { data: status, isLoading } = useQuery({
    queryKey: ['inspection-status', project?.id ?? 'all'],
    queryFn: async () => (await apiClient.get<{ items: StatusRow[] }>(
      '/api/v1/inspections/status',
      { params: project?.id ? { projectId: project.id } : {} },
    )).data.items,
  });

  /**
   * What has been recorded against the vehicle being inspected. An inspection
   * nobody can look up afterwards is a form, not a record.
   */
  const { data: history } = useQuery({
    queryKey: ['inspection-history', vehicleId],
    enabled: !!vehicleId,
    queryFn: async () => (await apiClient.get<{ items: HistoryRow[] }>(
      `/api/v1/inspections/vehicle/${vehicleId}`)).data.items,
  });

  // PR-ODO — carry the truck's own reading in the moment one is picked. The
  // fleet already knows the number; asking for it from memory is how it came
  // to be missing from every job on record.
  useEffect(() => {
    if (!vehicleId) { setOdo(''); setOdoPrefilled(false); return; }
    let cancelled = false;
    apiClient.get<{ currentOdometerKm?: number }>(`/api/v1/vehicles/${vehicleId}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data.currentOdometerKm != null) { setOdo(String(r.data.currentOdometerKm)); setOdoPrefilled(true); }
      })
      .catch(() => { /* the field stays empty and required */ });
    return () => { cancelled = true; };
  }, [vehicleId]);

  const items = kind === 'periodic' ? (sheets?.periodic.items ?? []) : (sheets?.extraordinary.items ?? []);
  const due = useMemo(
    () => (status ?? []).filter((r) => r.due_reason !== 'ok' && r.due_reason !== 'inactive'),
    [status],
  );
  const counts = useMemo(() => {
    const all = status ?? [];
    return {
      total: all.length,
      dueNow: all.filter((r) => r.due_reason === 'after_change').length,
      overdue: all.filter((r) => r.due_reason === 'overdue' || r.due_reason === 'never').length,
      ok: all.filter((r) => r.due_reason === 'ok').length,
      inactive: all.filter((r) => r.due_reason === 'inactive').length,
    };
  }, [status]);

  /** Parts marked on the diagram, written out for the "what changed" note. */
  const { data: catalog } = useQuery({
    queryKey: ['parts-catalog', partSearch],
    enabled: kind === 'extraordinary' && changed,
    queryFn: async () => (await apiClient.get<Array<{
      id: string; name_en: string; part_number: string | null;
      last_purchase_price_sar: string | null; total_stock: string | null;
    }>>('/api/v1/maintenance/parts', { params: partSearch ? { search: partSearch } : {} })).data,
  });

  /** Send one picture for one part, straight away. */
  const uploadPhoto = async (partKey: string, partLabel: string, file: File) => {
    if (!vehicleId) { setErr('Pick a vehicle first — a picture has to belong to a truck.'); return; }
    setErr(null); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('vehicleId', vehicleId);
      fd.append('partKey', partKey);
      fd.append('partLabel', partLabel);
      fd.append('file', file);
      const { data } = await apiClient.post<{ id: string }>('/api/v1/inspections/photos', fd);
      setPhotos((m) => ({ ...m, [partKey]: [...(m[partKey] ?? []), { id: data.id }] }));
    } catch (e) {
      const m = (e as { response?: { data?: { message?: string; error?: string } } }).response?.data;
      setErr(m?.message ?? m?.error ?? 'The picture could not be uploaded.');
    } finally { setUploading(false); }
  };

  const removePhoto = async (partKey: string, id: string) => {
    try { await apiClient.delete(`/api/v1/inspections/photos/${id}`); } catch { /* gone is gone */ }
    setPhotos((m) => ({ ...m, [partKey]: (m[partKey] ?? []).filter((x) => x.id !== id) }));
  };

  const photoCounts = useMemo(
    () => Object.fromEntries(Object.entries(photos).map(([k, v]) => [k, v.length])),
    [photos]);

  const flagged = useMemo(
    () => truckParts(axles).filter((p) => parts[p.key] && parts[p.key] !== 'ok'),
    [parts, axles],
  );

  const save = useMutation({
    mutationFn: async () => {
      if (!vehicleId) throw new Error('Pick a vehicle.');
      // PR-ODO — the reading is what every service interval and every defect
      // job is counted from. It arrives pre-filled from the truck, so this
      // only stops a form where it was deliberately cleared.
      if (!odo.trim() || Number(odo) <= 0) {
        throw new Error('Record the odometer reading — every service interval and defect job is counted from it.');
      }
      // Work with no cost is a maintenance record nobody can total.
      if (kind === 'extraordinary' && changed && workTypes.length && !(Number(workCost) > 0)) {
        throw new Error('Enter what the work cost — a maintenance record without a cost cannot be totalled.');
      }
      const payload = {
        vehicleId,
        kind,
        // Claim the pictures taken while this form was open.
        photoIds: Object.values(photos).flat().map((x) => x.id),
        odometerKm: odo.trim() === '' ? null : Number(odo),
        result: Object.values({ ...checks, ...(kind === 'extraordinary' ? parts : {}) })
          .some((v) => v === 'fail') ? 'fail'
          : Object.values({ ...checks, ...(kind === 'extraordinary' ? parts : {}) })
            .some((v) => v === 'attention') ? 'pass_with_notes' : 'pass',
        changedAnything: kind === 'extraordinary' ? changed : false,
        whatChanged: kind === 'extraordinary' && changed
          // The diagram already says which parts; the box adds why.
          ? [flagged.map((p) => p.label).join(', '), whatChanged.trim()].filter(Boolean).join(' — ')
          : null,
        items: [
          ...items.map((i) => ({ key: i.key, label: i.label, state: checks[i.key] ?? 'ok' })),
          ...(kind === 'extraordinary'
            ? truckParts(axles).map((p) => ({
                key: `part:${p.key}`,
                // The letter rides with the label, so a report read later says
                // "Left - rear wheel [C]" rather than just "fail".
                label: p.label + (codes[p.key] ? ` [${codes[p.key]}]` : ''),
                state: parts[p.key] ?? 'ok',
              }))
            : []),
        ],
        notes: [notes.trim(), kind === 'extraordinary'
          ? `Vehicle type: ${BODY_LABEL[bodyType]} · ${axles} axles` : '']
          .filter(Boolean).join(' — ') || null,
      };
      const res = await apiClient.post<{ id: string; code: string }>('/api/v1/inspections', payload);

      /*
       * The work goes to the maintenance ledger, not into the inspection row.
       *
       * A cost recorded only on an inspection would be invisible to every
       * maintenance report, every per-vehicle total and every driver
       * attribution already built on maintenance_events. Same endpoint the
       * quick log uses, so one oil change is one oil change wherever it was
       * entered from.
       */
      if (kind === 'extraordinary' && workTypes.length) {
        try {
          const note = [
            `From inspection ${res.data.code}`,
            flagged.length ? flagged.map((p) => p.label).join(', ') : '',
            whatChanged.trim(),
          ].filter(Boolean).join(' — ');

          /*
           * One event per job, so type-based reports count a tyre change as a
           * tyre change. The cost sits on the first alone - split across all of
           * them it would be invented, and repeated on each it would be charged
           * to this truck several times over.
           */
          let firstId: string | null = null;
          for (const [i, t] of workTypes.entries()) {
            const ev = await apiClient.post<{ id: string }>('/api/v1/maintenance/events', {
              vehicleId,
              eventType: t,
              priority: 'normal',
              description: workTypes.length > 1
                ? `${note} — done with: ${workTypes.filter((x) => x !== t).join(', ')}`
                : note,
              ...(odo.trim() && { odometerAtService: Number(odo) }),
              ...(i === 0 && Number(workCost) > 0 && { otherCostSar: Number(workCost) }),
            });
            if (i === 0) firstId = ev.data.id;
          }

          // Parts go onto the job that carries the cost, and the same call
          // draws them out of stock.
          if (firstId && usedParts.length) {
            for (const up of usedParts) {
              await apiClient.post(`/api/v1/maintenance/events/${firstId}/parts-used`, {
                ...(up.free
                  // Bought in for the job: recorded by name, and no stock is
                  // moved, because it never sat in stock.
                  ? { partNameText: up.name, source: 'fresh_purchase' }
                  : { partId: up.id, source: 'inventory' }),
                quantity: up.qty,
                unitCostSar: up.cost,
              });
            }
          }

          if (firstId) {
            const trouble = await commitConsumables(firstId, vehicleId, consumables);
            // The inspection is already saved; a short drum must not undo it.
            if (trouble.length) setErr(trouble.join(' '));
          }

          // The bill hangs on the record that carries the cost.
          if (bill && firstId) {
            const fd = new FormData();
            fd.append('file', bill);
            await apiClient.post(`/api/v1/maintenance/events/${firstId}/invoices`, fd);
          }
        } catch {
          // The inspection is already saved and is the record that matters.
          // Say the work did not log rather than failing the whole thing.
          setErr('The inspection was recorded, but the maintenance log failed — add it from the maintenance page.');
        }
      }
      return res;
    },
    onSuccess: (r) => {
      const d = r.data as {
        code?: string;
        defectsRaised?: Array<{ code: string; part: string }>;
        defectsAlreadyOpen?: string[];
      };
      const code = d?.code;
      // PR-DEFECT — say what the inspection set in motion. A checklist that
      // quietly opened four maintenance jobs is worse than one that says so.
      const raised = d?.defectsRaised ?? [];
      const already = d?.defectsAlreadyOpen ?? [];
      setMsg([
        `Recorded ${code ?? ''}.`,
        kind === 'extraordinary' && changed ? 'A periodic inspection is now due for this vehicle.' : '',
        raised.length
          ? `${raised.length} maintenance job${raised.length === 1 ? '' : 's'} opened for the defects found: ` +
            raised.map((x) => `${x.code} (${x.part})`).join(', ') + '.'
          : '',
        already.length
          ? `${already.length} defect${already.length === 1 ? ' already had an open job' : 's already had open jobs'}: ${already.join(', ')}.`
          : '',
      ].filter(Boolean).join(' '));
      setErr(null);
      setVehicleId(''); setOdo(''); setNotes(''); setWhatChanged('');
      setChanged(false); setParts({}); setChecks({}); setCodes({});
      setPhotos({}); setOpenPart(null); setWorkTypes([]); setWorkCost(''); setBill(null);
      setUsedParts([]); setPartSearch(''); setConsumables(EMPTY_CONSUMABLES);
      void qc.invalidateQueries({ queryKey: ['maintenance-events'] });
      void qc.invalidateQueries({ queryKey: ['inspection-status'] });
    },
    onError: (e) => { setErr(extractErrorMessage(e)); setMsg(null); },
  });

  return (
    <div className="space-y-6">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setPrinting(true)}>
            <Printer className="h-4 w-4" /> Print blank sheet
          </Button>
        </div>

        {printing && <PrintableInspectionSheet onClose={() => setPrinting(false)} />}

        {/* Counted, not left to be counted. */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {([
            ['Vehicles on this side', counts.total, 'text-foreground'],
            ['Due after a change', counts.dueNow, 'text-rose-600 dark:text-rose-400'],
            ['Overdue or never', counts.overdue, 'text-amber-600 dark:text-amber-400'],
            ['Up to date', counts.ok, 'text-emerald-600 dark:text-emerald-400'],
            ['Out of service', counts.inactive, 'text-muted-foreground'],
          ] as const).map(([label, n, tone]) => (
            <div key={label} className="rounded-xl border border-border bg-card px-4 py-3">
              <div className={`text-2xl font-mono ${tone}`}>{n}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>

        {/* What needs doing, before anything is offered to record. */}
        <section className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="font-medium">Due now</span>
            <span className="text-sm text-muted-foreground">
              {isLoading ? 'checking…' : `${due.length} of ${status?.length ?? 0} vehicles`}
            </span>
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-start px-3 py-2 font-medium">Vehicle</th>
                  <th className="text-start px-3 py-2 font-medium">Driver</th>
                  <th className="text-start px-3 py-2 font-medium">Side</th>
                  <th className="text-start px-3 py-2 font-medium">Last periodic</th>
                  <th className="text-start px-3 py-2 font-medium">Next due</th>
                  <th className="text-start px-3 py-2 font-medium">Why</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {due.length === 0 && !isLoading && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    Every vehicle is up to date.
                  </td></tr>
                )}
                {due.map((r) => (
                  <tr key={r.vehicle_id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono whitespace-nowrap">{r.plate_en ?? r.vehicle_code}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.driver_name ?? '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.side ?? 'no side'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{day(r.last_periodic_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{day(r.next_periodic_due)}</td>
                    <td className={`px-3 py-2 whitespace-nowrap ${DUE_TONE[r.due_reason]}`}>{DUE_LABEL[r.due_reason]}</td>
                    <td className="px-3 py-2 text-end">
                      <Button size="sm" variant="outline"
                        onClick={() => { setVehicleId(r.vehicle_id); setKind('periodic'); setMsg(null); setErr(null); }}>
                        Inspect
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* the two sheets */}
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {([
            ['periodic', 'Periodic inspection', ClipboardCheck, 'every 14 days'],
            ['extraordinary', 'Extraordinary inspection', Wrench, 'after work on the vehicle'],
          ] as const).map(([k, label, Icon, hint]) => (
            <button key={k} type="button" onClick={() => { setKind(k); setMsg(null); setErr(null); }}
              className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 ${
                kind === k ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon className="h-4 w-4" />
              <span>{label}</span>
              <span className="text-[11px] text-muted-foreground">· {hint}</span>
            </button>
          ))}
        </div>

        <section className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Vehicle *</label>
              {/* The same picker as everywhere else: grouped by side, searchable
                  by plate, code or driver. A bare list of 62 plates is unusable
                  on a phone in a yard. */}
              <VehiclePicker
                value={vehicleId || null}
                onChange={(id, v) => {
                  setVehicleId(id ?? '');
                  setMsg(null); setErr(null);
                  const t = (v as { vehicleType?: string } | null)?.vehicleType as BodyType | undefined;
                  if (t && t in BODY_LABEL) { setBodyType(t); setAxles(DEFAULT_AXLES[t]); }
                }}
                placeholder="Search a plate…"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground" htmlFor="ins-odo">
                Odometer km <span className="text-rose-600 dark:text-rose-400">*</span>
              </label>
              <Input id="ins-odo" value={odo} onChange={(e) => { setOdo(e.target.value); setOdoPrefilled(false); }} inputMode="decimal" />
              {odoPrefilled && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  From this truck&rsquo;s last reading — correct it if it has moved.
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground" htmlFor="ins-notes">Notes</label>
              <Input id="ins-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          {/* The diagram belongs to the extraordinary sheet: it is there because
              something was done to the truck and the whole vehicle is back in
              question. */}
          {kind === 'extraordinary' && (
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm font-medium">Tap the part that was worked on</div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded-sm bg-muted border border-border" /> ok</span>
                  <span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded-sm bg-amber-500/30 border border-amber-500" /> attention</span>
                  <span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded-sm bg-rose-500/30 border border-rose-500" /> fail</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Vehicle type</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(Object.keys(BODY_LABEL) as BodyType[]).map((t) => (
                    <button key={t} type="button"
                      onClick={() => { setBodyType(t); setAxles(DEFAULT_AXLES[t]); }}
                      aria-pressed={bodyType === t}
                      className={`rounded-lg border px-3 py-3 text-sm text-center transition ${
                        bodyType === t
                          ? 'border-gold text-foreground bg-muted/40'
                          : 'border-border text-muted-foreground hover:text-foreground hover:border-ring/40'}`}>
                      {BODY_LABEL[t]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Axles</span>
                <div className="inline-flex rounded-md border border-border overflow-hidden">
                  {([2, 3] as AxleCount[]).map((n) => (
                    <button key={n} type="button" onClick={() => setAxles(n)}
                      className={`px-3 py-1 ${axles === n ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                      {n}
                    </button>
                  ))}
                </div>
                <span className="text-muted-foreground">
                  — 2: rigid truck, 2 rear axles on duals, 8 tyres at the back. 3: tractor and trailer, 12 tyres in all
                </span>
              </div>
              <TruckDiagram
                axles={axles}
                bodyType={bodyType}
                states={parts}
                codes={codes}
                photoCounts={photoCounts}
                onSelect={(k) => setOpenPart(k)}
                onToggle={(k, next) => setParts((p) => ({ ...p, [k]: next }))}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Press any part of the truck — a wheel, a door, the tank — to mark it and attach a photo.
              </p>

              {/*
                One part, opened.

                Everything about a part is decided in one place: whether it is
                sound, what is wrong with it, and the picture proving it. Cycling
                a colour by tapping told you a wheel was bad but never which
                wheel of a pair, nor what "bad" meant, and left nowhere to put a
                photograph.

                The picture uploads the moment it is chosen. An inspector stands
                at a truck with one hand free; making them finish a form before
                the evidence is safe is how evidence gets lost.
              */}
              {openPart && (() => {
                const part = truckParts(axles).find((x) => x.key === openPart);
                if (!part) return null;
                const state = parts[openPart] ?? 'ok';
                const shots = photos[openPart] ?? [];
                return (
                  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
                       role="dialog" aria-modal="true" aria-label={part.label}
                       onClick={() => setOpenPart(null)}>
                    <div className="bg-card border border-border w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4 space-y-4 max-h-[88vh] overflow-y-auto"
                         onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-medium">{part.label}</div>
                        <button type="button" onClick={() => setOpenPart(null)}
                          className="text-muted-foreground hover:text-foreground text-sm">Close</button>
                      </div>

                      {/* Its condition. */}
                      <div>
                        <div className="text-xs text-muted-foreground mb-1.5">Condition</div>
                        <div className="grid grid-cols-3 gap-2">
                          {([
                            ['ok', 'Sound', 'border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'],
                            ['attention', 'Watch', 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400'],
                            ['fail', 'Damaged', 'border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-400'],
                          ] as const).map(([val, label, on]) => (
                            <button key={val} type="button"
                              onClick={() => setParts((m) => ({ ...m, [openPart]: val }))}
                              className={`h-10 rounded-lg border text-sm transition ${
                                state === val ? on : 'border-border hover:bg-muted/50'}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* What is wrong with it, in the letters from the sheet. */}
                      {state !== 'ok' && (
                        <div>
                          <div className="text-xs text-muted-foreground mb-1.5">What is wrong</div>
                          <div className="flex flex-wrap gap-1.5">
                            {DAMAGE_CODES.map((d) => (
                              <button key={d.code} type="button"
                                onClick={() => setCodes((m) => {
                                  const next = { ...m };
                                  if (next[openPart] === d.code) delete next[openPart];
                                  else next[openPart] = d.code;
                                  return next;
                                })}
                                className={`px-2.5 h-8 rounded-md border text-xs transition ${
                                  codes[openPart] === d.code
                                    ? 'border-primary bg-primary/10 text-foreground'
                                    : 'border-border text-muted-foreground hover:bg-muted/50'}`}>
                                <b className="font-mono">{d.code}</b> {d.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* The evidence. */}
                      <div>
                        <div className="text-xs text-muted-foreground mb-1.5">
                          Photos {shots.length > 0 && `(${shots.length})`}
                        </div>
                        {shots.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            {shots.map((ph) => (
                              <div key={ph.id} className="relative group">
                                <img src={`/api/v1/inspections/photos/${ph.id}`} alt={part.label}
                                  className="w-full h-20 object-cover rounded-md border border-border" />
                                <button type="button" onClick={() => removePhoto(openPart, ph.id)}
                                  className="absolute top-1 end-1 h-6 w-6 rounded-full bg-black/60 text-white text-xs leading-none"
                                  aria-label="Remove this photo">×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <label className={`flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-border text-sm cursor-pointer hover:bg-muted/40 transition ${
                          uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                          <Camera className="h-4 w-4" />
                          {uploading ? 'Uploading…' : 'Take or choose a photo'}
                          {/* capture hints a phone toward its camera; on a desktop
                              it is ignored and the file chooser opens instead. */}
                          <input type="file" accept="image/*" capture="environment" className="sr-only"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) void uploadPhoto(openPart, part.label, f);
                              e.target.value = '';
                            }} />
                        </label>
                        <p className="text-[11px] text-muted-foreground mt-1.5">
                          Saved against this vehicle straight away, and kept with the inspection once you record it.
                        </p>
                      </div>

                      <Button variant="gold" className="w-full" onClick={() => setOpenPart(null)}>Done</Button>
                    </div>
                  </div>
                );
              })()}

              {/* The codes from the printed sheet. Shown only once something is
                  marked, because until then there is nothing to describe. */}
              {flagged.length > 0 && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Mark the damage found
                  </div>
                  {flagged.map((p) => (
                    <div key={p.key} className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="text-sm">{p.label}</span>
                      <div className="inline-flex rounded-md border border-border overflow-hidden">
                        {DAMAGE_CODES.map((d) => (
                          <button key={d.code} type="button" title={d.label}
                            onClick={() => setCodes((c) => (
                              c[p.key] === d.code
                                ? Object.fromEntries(Object.entries(c).filter(([k]) => k !== p.key)) as Record<string, DamageCode>
                                : { ...c, [p.key]: d.code }))}
                            className={`px-2 py-1 text-[11px] font-mono ${
                              codes[p.key] === d.code
                                ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400'
                                : 'text-muted-foreground hover:text-foreground'}`}>
                            {d.code}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-muted-foreground">
                    {DAMAGE_CODES.map((d) => `${d.code} = ${d.label}`).join('   ')}
                  </div>
                </div>
              )}
              {/*
                  What was actually done, logged once.

                  Odometer and note are already asked for above, so they are not
                  asked again here - the same number entered twice is the same
                  number entered wrong once.
              */}

              {flagged.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Marked: <b className="text-foreground">{flagged.map((p) => p.label).join(', ')}</b>
                </div>
              )}
              <label className="flex items-start gap-2 text-sm cursor-pointer pt-2 border-t border-border">
                <input type="checkbox" checked={changed} onChange={(e) => setChangedAndClear(e.target.checked)} className="mt-0.5" />
                <span>
                  Something on this vehicle was <b>changed</b>
                  <span className="block text-xs text-muted-foreground">
                    A periodic inspection becomes due immediately — the last one no longer describes this truck.
                  </span>
                </span>
              </label>
              {changed && (
                <Input value={whatChanged} onChange={(e) => setWhatChanged(e.target.value)}
                  placeholder="What was changed? e.g. rear left tyre replaced" />
              )}
              {changed && (
              <div className="rounded-lg border border-border p-3 space-y-3">
                <div>
                  <div className="text-sm font-medium">What was done <span className="text-muted-foreground font-normal">(optional)</span></div>
                  <p className="text-xs text-muted-foreground">
                    Goes to this vehicle’s maintenance record, alongside the inspection.
                    Pick as many as were done.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {WORK_TYPES.map((t) => (
                    <button key={t.value} type="button"
                      aria-pressed={workTypes.includes(t.value)}
                      onClick={() => setWorkTypes((v) =>
                        v.includes(t.value) ? v.filter((x) => x !== t.value) : [...v, t.value])}
                      className={`h-11 rounded-lg border text-sm transition ${
                        workTypes.includes(t.value)
                          ? 'border-primary bg-primary/10 text-foreground font-medium'
                          : 'border-border text-muted-foreground hover:bg-muted/50'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {workTypes.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground" htmlFor="ins-cost">
                        Cost SAR <span className="text-rose-500">*</span>
                      </label>
                      <Input id="ins-cost" inputMode="decimal" value={workCost}
                        onChange={(e) => setWorkCost(e.target.value)} placeholder="0" />
                    </div>

                    {/* The bill. Filed as this event's invoice, which is where
                        every other maintenance bill goes and what the OCR and
                        the payment status already read. */}
                    <div>
                      <label className="text-xs text-muted-foreground">Bill (optional)</label>
                      {bill ? (
                        <div className="h-10 flex items-center gap-2 rounded-md border border-border px-3">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm truncate flex-1">{bill.name}</span>
                          <button type="button" onClick={() => setBill(null)}
                            className="text-xs text-muted-foreground hover:text-foreground shrink-0">Remove</button>
                        </div>
                      ) : (
                        <label className="h-10 flex items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm cursor-pointer hover:bg-muted/40 transition">
                          <Upload className="h-4 w-4" /> Upload bill
                          <input type="file" accept="image/*,application/pdf" className="sr-only"
                            onChange={(e) => { setBill(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                        </label>
                      )}
                    </div>

                    {/* Spare parts fitted. Picked from the catalogue, so the
                        shelf count follows the work rather than trailing it. */}
                    <div className="sm:col-span-2 space-y-2">
                      <div className="text-xs text-muted-foreground">Spare parts used (optional)</div>
                      {usedParts.length > 0 && (
                        <div className="rounded-md border border-border divide-y divide-border">
                          {usedParts.map((up) => (
                            <div key={up.id} className="flex items-center gap-2 px-2 py-1.5">
                              <span className="text-sm flex-1 truncate">
                                {up.name}
                                {up.free
                                  ? <span className="text-[11px] text-muted-foreground ms-1.5">one-off</span>
                                  : <span className="text-[11px] text-muted-foreground ms-1.5">{up.stock} in stock</span>}
                              </span>
                              <input type="number" min={1} value={up.qty} aria-label={`Quantity of ${up.name}`}
                                max={up.free ? undefined : Math.max(1, up.stock)}
                                onChange={(e) => setUsedParts((l) => l.map((x) => {
                                  if (x.id !== up.id) return x;
                                  const want = Math.max(1, Number(e.target.value) || 1);
                                  return { ...x, qty: x.free ? want : Math.min(want, Math.max(1, x.stock)) };
                                }))}
                                className="h-8 w-14 rounded border border-input bg-background px-1 text-sm text-center" />
                              <input type="number" min={0} step="0.01" value={up.cost} aria-label={`Unit cost of ${up.name}`}
                                onChange={(e) => setUsedParts((l) => l.map((x) =>
                                  x.id === up.id ? { ...x, cost: Math.max(0, Number(e.target.value) || 0) } : x))}
                                className="h-8 w-20 rounded border border-input bg-background px-1 text-sm text-end" />
                              <span className="text-xs text-muted-foreground w-20 text-end">
                                {(up.qty * up.cost).toLocaleString('en-US')} SAR
                              </span>
                              <button type="button" onClick={() => setUsedParts((l) => l.filter((x) => x.id !== up.id))}
                                className="text-xs text-muted-foreground hover:text-foreground">Remove</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Input value={partSearch} onChange={(e) => setPartSearch(e.target.value)}
                        placeholder="Search the parts catalogue…" />
                      {partSearch.trim() && (
                        <div className="rounded-md border border-border max-h-40 overflow-y-auto divide-y divide-border">
                          {(catalog ?? []).filter((c) => !usedParts.some((u) => u.id === c.id)).slice(0, 25).map((c) => (
                            <button key={c.id} type="button"
                              disabled={Number(c.total_stock ?? 0) <= 0}
                              onClick={() => {
                                setUsedParts((l) => [...l, {
                                  id: c.id, name: c.name_en, qty: 1,
                                  cost: Number(c.last_purchase_price_sar ?? 0),
                                  stock: Number(c.total_stock ?? 0), free: false,
                                }]);
                                setPartSearch('');
                              }}
                              className="w-full text-start px-2 py-1.5 hover:bg-muted/50 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                              <span className="text-sm flex-1 truncate">{c.name_en}</span>
                              <span className="text-[11px] text-muted-foreground font-mono">{c.part_number ?? ''}</span>
                              {/* Stock is shown because fitting a part you do not
                                  have is the thing this picker should prevent. */}
                              <span className={`text-[11px] ${Number(c.total_stock ?? 0) > 0
                                ? 'text-muted-foreground' : 'text-amber-600 dark:text-amber-400'}`}>
                                {Number(c.total_stock ?? 0)} in stock
                              </span>
                            </button>
                          ))}
                          {(catalog ?? []).filter((c) => !usedParts.some((u) => u.id === c.id)).length === 0 && (
                            <button type="button"
                              onClick={() => {
                                setUsedParts((l) => [...l, {
                                  id: `free:${partSearch.trim()}:${l.length}`,
                                  name: partSearch.trim(), qty: 1, cost: 0, stock: 0, free: true,
                                }]);
                                setPartSearch('');
                              }}
                              className="w-full text-start px-2 py-2 hover:bg-muted/50 transition">
                              <span className="text-sm">Add “{partSearch.trim()}” as a one-off part</span>
                              <span className="block text-[11px] text-muted-foreground">
                                Not in the catalogue — recorded on the job by name, with no stock movement.
                              </span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <ConsumablesPicker value={consumables} onChange={setConsumables}
                      enabled={kind === 'extraordinary' && changed} />

                    <p className="text-[11px] text-muted-foreground sm:col-span-2">
                      Odometer and note come from the fields above.
                      {usedParts.some((u) => !u.free) && ' Catalogue parts are drawn from stock when this is saved.'}
                      {workTypes.length > 1 && ' The cost is recorded once, against the first job, so this truck is not charged twice.'}
                    </p>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* the checklist */}
          <div className="rounded-lg border border-border divide-y divide-border">
            {items.map((i) => {
              const v = checks[i.key] ?? 'ok';
              return (
                <div key={i.key} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="text-sm">{i.label}</span>
                  <div className="inline-flex rounded-md border border-border overflow-hidden shrink-0">
                    {(['ok', 'attention', 'fail', 'na'] as PartState[]).map((s) => (
                      <button key={s} type="button" onClick={() => setChecks((c) => ({ ...c, [i.key]: s }))}
                        className={`px-2 py-1 text-[11px] ${v === s
                          ? s === 'fail' ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400'
                            : s === 'attention' ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                            : 'bg-muted text-foreground'
                          : 'text-muted-foreground hover:text-foreground'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {err && <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-400">{err}</div>}
          {msg && <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-2"><Check className="h-4 w-4" />{msg}</div>}

          {/* What this truck has had done. Shown beside the form, so the person
              filling it in can see whether it was already inspected today. */}
          {vehicleId && (history?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-3 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                History — {history!.length} record{history!.length === 1 ? '' : 's'}
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-border">
                {history!.map((h) => (
                  <div key={h.id} className="px-3 py-2 text-sm flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground">{h.code}</span>
                        <span className={h.kind === 'extraordinary' ? 'text-amber-600 dark:text-amber-400' : ''}>
                          {h.kind === 'extraordinary' ? 'Extraordinary' : 'Periodic'}
                        </span>
                        <span className={h.result === 'fail'
                          ? 'text-rose-600 dark:text-rose-400'
                          : 'text-muted-foreground'}>{h.result.replace(/_/g, ' ')}</span>
                        {h.changed_anything && (
                          <span className="text-[11px] rounded px-1.5 py-0.5 bg-rose-500/15 text-rose-600 dark:text-rose-400">
                            changed
                          </span>
                        )}
                      </div>
                      {h.what_changed && (
                        <div className="text-xs text-muted-foreground truncate">{h.what_changed}</div>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap text-end">
                      <div className="font-mono">{day(h.inspected_at)}</div>
                      <div>{h.inspected_by_name ?? ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button variant="gold" disabled={save.isPending || !vehicleId} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
            Record {kind === 'periodic' ? 'periodic' : 'extraordinary'} inspection
          </Button>
        </section>
    </div>
  );
}

/** The same panel on its own page, for the direct link. */
export default function InspectionsPage() {
  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Vehicle inspections</div>
          <Link to="/dashboard/maintenance">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button>
          </Link>
        </div>
      </header>
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-6 py-6">
        <InspectionsPanel />
      </main>
    </div>
  );
}
